<template>
    <div>
        <div id="editor">
            <div class="form-group">
                <div class="d-flex">
                    <select v-model="templateID" class="form-control form-control-sm">
                        <option :value="null">テンプレートを選択</option>
                        <option v-for="template in templates"
                                :value="template.id">
                            {{ template.title }}
                        </option>
                    </select>
                    <button
                        v-if="templates"
                        @click="insertTemplate"
                        style="width: 180px;"
                        class="btn btn-sm btn-yokumiru-user ml-2">
                        テンプレートを挿入
                    </button>
                </div>
            </div>

            <b-form-textarea
                  v-model="medical_record.body"
                  :rows="10"
                  :max-rows="10"
                  :class="{ 'is-invalid' : errors.body }"
                  id="medical_record_edit">
            </b-form-textarea>
            <div
                v-if="errors.body"
                class="invalid-feedback d-block mt-1">
                {{ errors.body[0] }}
            </div>
        </div>

        <div class="mt-3 d-flex justify-content-between align-items-center">
            <template v-if="!isHealthPackage">
                <div
                    v-if="!medical_record.published_at"
                    class="text-muted">
                    <div>記録未公開</div>
                </div>
                <div v-else>
                    <div class="yokumiru-dark-green">記録公開済</div>
                    <div class="text-muted">公開日時:&nbsp;{{ publishedAt }}</div>
                </div>
            </template>
            <template v-else>
                <div></div>
            </template>

            <div class="text-right">
                <button
                    class="btn btn-yokumiru-orange mr-1"
                    :disabled="inProgress"
                    @click="update">
                    保存
                </button>
                <button
                    v-if="canPublish && !isHealthPackage"
                    class="btn btn-yokumiru-dark-green"
                    :disabled="inProgress"
                    @click="publish">
                    保存して公開
                </button>
            </div>
        </div>
    </div>
</template>

<script>
    import YokumiruHelper from '../../YokumiruHelper'
    import constants from '../../constants'
    import moment from 'moment-timezone'

    export default {
        props: {
            consultation: {
                type: Object,
                required: true,
            },
            medicalRecord: {
                type: Object,
                required: true,
            },
        },

        data: function() {
            return {
                helper: YokumiruHelper,
                templateID: null,
                templates: null,
                medical_record: { body : '' },
                errors: { body: null },
                inProgress: false,
            };
        },

        computed: {
            /**
             * 公開日フォーマット
             *
             * @return {string}
             */
            publishedAt: function () {
                if (this.medical_record && this.medical_record.published_at) {
                    return moment(this.medical_record.published_at).locale('ja').format('YYYY年M月D日(ddd) HH:mm')
                }
                return ''
            },

            /**
             * 記録公開可能判定
             *
             * @return {boolean}
             */
            canPublish: function () {
                return !this.medical_record.published_at && constants.consultations.canPublishAll.includes(this.consultation.status)
            },

            /**
             * 健康管理パッケージ判定
             *
             * @return {boolean}
             */
            isHealthPackage: function () {
                return this.consultation.doctor_menu && this.consultation.doctor_menu.is_health_package
            },
        },

        watch: {
            'medical_record.body': _.debounce(function () {
                bus.$emit('medical-record-debounce', this.medical_record.body)
            }, 500)
        },

        created: function() {
            this.medical_record = this.medicalRecord
            this.initMedicalRecord()
            this.fetchMedicalRecordTemplates()

            bus.$on('consultation-feedback-published', this.fetchConsultation)
            bus.$on('medical-record-validation-failed', this.setValidationError)
        },

        beforeDestroy: function () {
            bus.$off('consultation-feedback-published')
            bus.$off('medical-record-validation-failed')
        },

        methods: {
            /**
             * 診療記録テンプレート取得
             */
            fetchMedicalRecordTemplates: function() {
                axios.get(constants.doctor.medical_record_templates.endpoint.base)
                    .then(({data}) => {
                        this.templates = data
                    })
            },

            /**
             * 選択中テンプレートの挿入
             */
            insertTemplate: function() {
                if (!this.templates || !this.templateID) {
                    return;
                }

                const template = this.templates.find(t => {
                    return t.id === this.templateID
                });

                // 入力中の文字があれば改行する
                if (this.medical_record
                    && this.medical_record.body
                    && this.medical_record.body.length > 0) {
                    this.medical_record.body += '\r'
                }
                this.medical_record.body += template.body

                this.$nextTick(() => document.getElementById('medical_record_edit').focus())
            },

            /**
             * 診療記録の初期値設定
             */
            initMedicalRecord: function () {
                if (typeof this.medical_record.body === 'undefined' || this.medical_record.body == null) {
                    this.medical_record.body = ''
                }
                bus.$emit('medical-record-debounce', this.medical_record.body)
            },

            /**
             * 診療記録更新
             */
            update: async function() {
                if (this.inProgress) return
                this.inProgress = true

                const endpoint = `${constants.doctor.medical_records.endpoint.base}/${this.medical_record.id}`

                await axios.post(endpoint, this.medical_record)
                    .then(({data}) => {
                        this.medical_record = data.data
                        this.initMedicalRecord()
                        this.errors = { body: null }
                        bus.$emit('alert-success', '記録を保存しました。')
                    })
                    .catch(() => {
                        bus.$emit('alert-danger', '保存に失敗しました。時間を置いて再度お試しください。')
                    })

                this.inProgress = false
            },

            /**
             * 診療記録公開
             */
            publish: async function () {
                if (this.inProgress) return
                this.inProgress = true

                const endpoint = `${constants.doctor.consultations.endpoint.base}/${this.consultation.id}/publish`
                const data = {
                    body: this.medical_record.body,
                }

                await axios.put(endpoint, data)
                    .then(() => {
                        this.errors = { body: null }
                        this.fetchConsultation()
                        bus.$emit('alert-success', '記録を公開しました。')
                    })
                    .catch(() => {
                        bus.$emit('alert-danger', '保存に失敗しました。時間を置いて再度お試しください。')
                    })

                this.inProgress = false
            },

            /**
             * 診療予約の取得
             */
            fetchConsultation: function () {
                this.errors = { body: null }

                axios.get(`${constants.doctor.consultations.endpoint.base}/${this.consultation.id}`)
                    .then(({data}) => {
                        this.medical_record = data.medical_record
                        this.initMedicalRecord()
                    })
            },

            setValidationError: function () {
                this.errors.body = [`${this.$locale.consultation_host}記録を入力してください。`]
            },
        },
    }
</script>
